import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import Header from '../../components/common/Header'
import AuthBrandPanel from '../../components/auth/AuthBrandPanel'
import { useTranslation } from 'react-i18next'


const CreatePassword = () => {
  const navigate = useNavigate()
  const { userType } = useParams()
  const { token } = useParams()
  const location = useLocation()

  const verificationId = location.state?.verificationId || localStorage.getItem('verification_id')
  // userData is no longer directly used here as the new API flow returns user details on successful password creation
  // const userData = location.state?.userData

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  })
  const { t, i18n } = useTranslation();
  const [isRTL, setIsRTL] = useState(i18n.language === 'ar');
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
    requirements: {
      minLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecial: false
    }
  })

  // User type configurations - EXACT match to Register component
  const userTypeConfig = {
    pet_guardian: {
      title: t("register.roles.pet_guardian.title"),
      subtitle: t("register.roles.pet_guardian.subtitle"),
      primaryColor: '#8548A8',
      buttonColor: '#8548A8',
      mainCircleGradient: 'linear-gradient(135deg, #8548A8 0%, #2A8AD2 100%)',
      floatingCircle1: 'linear-gradient(135deg, #FFA055 0%, #8548A8 100%)',
      floatingCircle2: '#8548A8',
      floatingCircle3: 'linear-gradient(135deg, #FFA055 0%, #FF6B9D 100%)',
      floatingCircle4: '#2A8AD2',
      floatingCircle5: 'rgba(255, 160, 85, 0.6)'
    },
    veterinarian: {
      title: t("register.roles.veterinarian.title"),
      subtitle: t("register.roles.veterinarian.subtitle"),
      primaryColor: '#00D4B3',
      buttonColor: '#00D4B3',
      mainCircleGradient: 'linear-gradient(135deg, #2A8AD2 0%, #00D483 100%)',
      floatingCircle1: 'linear-gradient(135deg, #00D4B3 0%, #2A8AD2 100%)',
      floatingCircle2: '#00D4B3',
      floatingCircle3: 'linear-gradient(135deg, #2A8AD2 0%, #00D4B3 100%)',
      floatingCircle4: '#2A8AD2',
      floatingCircle5: 'rgba(0, 212, 179, 0.6)'
    },
    service_provider: {
      title: t("register.roles.service_provider.title"),
      subtitle: t("register.roles.service_provider.subtitle"),
      primaryColor: '#D65745',
      buttonColor: '#D65745',
      mainCircleGradient: 'linear-gradient(135deg, #D65745 0%, #00D483 100%)',
      floatingCircle1: 'linear-gradient(135deg, #D65745 0%, #00D4B3 100%)',
      floatingCircle2: '#D65745',
      floatingCircle3: 'linear-gradient(135deg, #00D4B3 0%, #D65745 100%)',
      floatingCircle4: '#00D4B3',
      floatingCircle5: 'rgba(214, 87, 69, 0.6)'
    },
    firm: {
      title: t("register.roles.firm.title"),
      subtitle: t("register.roles.firm.subtitle"),
      primaryColor: "#000080",
      buttonColor: "#000080",
      mainCircleGradient: "linear-gradient(135deg, #000080 0%, #2A8AD2 100%)",
      floatingCircle1: "linear-gradient(135deg, #000080 0%, #FFA055 100%)",
      floatingCircle2: "#000080",
      floatingCircle3: "linear-gradient(135deg, #2A8AD2 0%, #000080 100%)",
      floatingCircle4: "#2A8AD2",
      floatingCircle5: "rgba(0, 0, 128, 0.6)",
    }
  }

  const config = userTypeConfig[userType] || userTypeConfig.pet_guardian

  // ✅ Check if verification_id exists, redirect if not (skip when token is present)
  useEffect(() => {
    if (!verificationId && token == null) {
      console.error('No verification ID found. Redirecting to registration.')
      navigate(`/auth/register/${userType}`)
    }
  }, [verificationId, navigate, userType])

  // Validate password strength
  const validatePassword = (password) => {
    const requirements = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    }

    const score = Object.values(requirements).filter(Boolean).length
    const feedback = []

    if (!requirements.minLength) feedback.push(t('resetPassword.passwordStrength.length'))
    if (!requirements.hasUppercase) feedback.push(t('resetPassword.passwordStrength.uppercase'))
    if (!requirements.hasLowercase) feedback.push(t('resetPassword.passwordStrength.lowercase'))
    if (!requirements.hasNumber) feedback.push(t('resetPassword.passwordStrength.number'))
    if (!requirements.hasSpecial) feedback.push(t('resetPassword.passwordStrength.special'))

    return { score, feedback, requirements }
  }

  useEffect(() => {
    setPasswordStrength(validatePassword(formData.password))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language])
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      const newIsRTL = lng === 'ar';
      setIsRTL(newIsRTL);
      document.documentElement.dir = newIsRTL ? 'rtl' : 'ltr';
      document.documentElement.lang = lng;
    };
    handleLanguageChange(i18n.language);
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setError('')

    // Update password strength for password field
    if (name === 'password') {
      setPasswordStrength(validatePassword(value))
    }
  }

  // Get strength color
  const getStrengthColor = (score) => {
    if (score <= 2) return '#EF4444' // Red
    if (score <= 3) return '#F59E0B' // Yellow
    if (score <= 4) return '#10B981' // Green
    return '#059669' // Dark Green
  }

  // Get strength text
  const getStrengthText = (score) => {
    if (score === 0) return t('resetPassword.passwordStrength.labels.prompt')
    if (score <= 2) return t('resetPassword.passwordStrength.labels.weak')
    if (score <= 3) return t('resetPassword.passwordStrength.labels.fair')
    if (score <= 4) return t('resetPassword.passwordStrength.labels.good')
    return t('resetPassword.passwordStrength.labels.strong')
  }

  const requirementLabelMap = {
    minLength: t('resetPassword.passwordStrength.badges.minLength'),
    hasUppercase: t('resetPassword.passwordStrength.badges.uppercase'),
    hasLowercase: t('resetPassword.passwordStrength.badges.lowercase'),
    hasNumber: t('resetPassword.passwordStrength.badges.number'),
    hasSpecial: t('resetPassword.passwordStrength.badges.special')
  }

  const handleCreatePassword = async () => {
    // Validate passwords
    if (!formData.password) {
      setError(t('resetPassword.form.errors.required'))
      return
    }

    if (passwordStrength.score < 5) {
      setError(t('resetPassword.form.errors.notStrong'))
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('resetPassword.form.errors.mismatch'))
      return
    }

    // Only enforce verificationId when no token is provided
    if (!verificationId && token == null) {
      setError(t('resetPassword.errors.sessionExpired'))
      navigate(`/auth/register/${userType}`)
      return
    }

    setIsLoading(true)
    setError('')

    try {
      if (token != null) {
      } else {
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

      const requestData = token != null
        ? {
          password: formData.password,
          password_confirmation: formData.confirmPassword,
          token: token
        }
        : {
          verification_id: verificationId,
          password: formData.password,
          password_confirmation: formData.confirmPassword
        }

      var endpoint = "";
      if (token != null) {
        endpoint = "/api/v1/auth/register/create-password-with-token"
      } else {
        endpoint = "/api/v1/auth/register/create-password"
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      })
      // Check if response is JSON
      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        console.error('Response is not JSON:', contentType)
        const textResponse = await response.text()
        console.error('Response text:', textResponse)
        throw new Error('Server returned invalid response format')
      }

      const data = await response.json()

      if (response.ok) {
        // Check for success in multiple possible response formats
        const isSuccess = data.status === 'success' ||
          data.success === true ||
          response.status === 200 ||
          response.status === 201

        if (isSuccess) {

          // Handle nested data structure (data.data) or flat structure (data)
          const responseData = data.data || data

          // Store authentication tokens
          if (responseData.access_token) {
            localStorage.setItem('access_token', responseData.access_token)
          }

          if (responseData.refresh_token) {
            localStorage.setItem('refresh_token', responseData.refresh_token)
          }

          if (responseData.user) {
            localStorage.setItem('user_data', JSON.stringify(responseData.user))
          } else {
            localStorage.setItem('user_data', JSON.stringify(responseData.user_data))
          }

          // Clean up verification_id
          localStorage.removeItem('verification_id')

          // Navigate to profile completion or dashboard
          const userFromResponse = responseData.user_data
          if (userFromResponse) {
            // Navigate to profile completion step
            navigate(`/auth/complete-profile/${userType}`, {
              state: {
                user: userFromResponse,
                passwordCreated: true,
                autoLoggedIn: true
              }
            })
          } else {
            // Fallback to dashboard if no user data
            navigate('/dashboard')
          }
        } else {
          throw new Error(data.message || 'Password creation failed')
        }
      } else {
        // Handle error responses
        console.error('Password creation failed:', response.status, data)

        if (response.status === 400) {
          if (data.message && (data.message.includes('verification') || data.message.includes('expired'))) {
            setError(t('resetPassword.errors.sessionExpired'))
            setTimeout(() => {
              navigate(`/auth/register/${userType}`)
            }, 2000)
          } else {
            setError(data.message || t('resetPassword.form.errors.general'))
          }
        } else if (response.status === 422) {
          // Handle validation errors
          if (data.errors) {
            const errorMessages = Object.values(data.errors).flat()
            setError(errorMessages.join('. '))
          } else {
            setError(data.message || t('resetPassword.form.errors.notStrong'))
          }
        } else if (response.status === 500) {
          setError(t('resetPassword.form.errors.server'))
        } else {
          setError(data.message || t('resetPassword.form.errors.general'))
        }
      }
    } catch (error) {
      console.error('Password creation error:', error)

      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError(t('resetPassword.form.errors.network'))
      } else if (error.message.includes('JSON')) {
        setError(t('resetPassword.form.errors.server'))
      } else if (error.message.includes('invalid response format')) {
        setError(t('resetPassword.form.errors.invalidResponse'))
      } else {
        setError(error.message || t('resetPassword.form.errors.network'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  // ✅ REMOVED: All the old complex fallback logic (handleDirectPasswordUpdate)
  // ✅ REMOVED: updateRegistrationProgress function as it's no longer part of this flow

  return (

    <div
      className="create-password-responsive-root"
      dir={isRTL ? "rtl" : "ltr"}
      lang={i18n.language} style={{
        width: '100vw',
        height: '100vh',
        background: '#FAFAFA',
        position: 'fixed',
        top: 0,
        left: 0,
        fontFamily: '"Red Hat Display", sans-serif',
        overflow: 'auto',
        display: 'flex'
      }}>
      {/* Header Component */}
      {/* Header Component - Inline */}
      <div
        className="create-password-header"
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          width: "100%",
          height: "100px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 60px",
          zIndex: 10,
          boxSizing: "border-box",
        }}
      >
        {/* Logo */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
          }}
          onClick={() => navigate("/")}
        >
          <img src="/Landing page assets/Sulhafa_Logo_White.png" alt="Sulhafa" style={{ height: '90px', width: 'auto', display: 'block' }} />
          <img src="/Landing page assets/Sulhafa_Logo_Text_White.png" alt="Sulhafa" style={{ height: '68px', width: 'auto', display: 'block' }} />
        </div>

      </div>

      {/* Left Section - Design Panel - EXACT match to Register */}
      <div className="create-password-left-panel" style={{
        width: '50%',
        height: '100vh',
        background: '#FEFEFA',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 0,
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <AuthBrandPanel
          color={config.primaryColor}
          title={t("login.caring")}
          image={userType === "firm"
            ? "/Landing page assets/firms_scene.png"
            : `/Landing page assets/${userType === "pet_guardian"
              ? "road friend2_scene.jpg"
              : userType === "veterinarian"
                ? "vet (1)_scene.png"
                : "shop_scene.png"
              }`
          }
          imageAlt={`${config.title} character`}
          imageShape="scene"
          isRTL={isRTL}
        />
      </div>


      {/* Right Section - Password Creation Form */}
      <div className="create-password-right-panel" style={{
        width: '50%',
        height: '100vh',
        background: '#FFFFFF',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'auto'
      }}>
        {/* Progress Steps - Updated for step 2 with colored numbers */}
        <div style={{
          position: 'absolute',
          top: '140px',
          left: '0',
          right: '0',
          width: '100%',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 80px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '600px'
          }}>
            {/* Step 1 - Completed */}
            <div style={{
              width: '40px',
              height: '40px',
              background: config.primaryColor,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '18px',
              color: '#FFFFFF',
              zIndex: 2
            }}>
              1
            </div>

            {/* Connecting Line 1-2 */}
            <div style={{
              flex: 1,
              height: '3px',
              background: config.primaryColor,
              margin: '0 20px'
            }} />

            {/* Step 2 - Active (Create Password) */}
            <div style={{
              width: '40px',
              height: '40px',
              background: config.primaryColor,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '18px',
              color: '#FFFFFF',
              zIndex: 2
            }}>
              2
            </div>

            {/* Connecting Line 2-3 */}
            <div style={{
              flex: 1,
              height: '3px',
              background: '#E5E7EB',
              margin: '0 20px'
            }} />

            {/* Step 3 - Inactive */}
            <div style={{
              width: '40px',
              height: '40px',
              background: '#E5E7EB',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '18px',
              color: '#9CA3AF',
              zIndex: 2
            }}>
              3
            </div>
          </div>
        </div>

        {/* Form Content Container - Left aligned within progress line width */}
        <div style={{
          position: 'absolute',
          top: '220px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: '600px',
          padding: '0 80px',
          boxSizing: 'border-box'
        }}>
          {/* Form Header - Left aligned */}
          <div style={{
            textAlign: isRTL ? "right" : "left",
            marginBottom: '16px'
          }}>
            <h2 style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '38px',
              lineHeight: '1.3',
              color: '#1F2937',
              margin: '0 0 12px 0'
            }}>
              {t('register.createpassword')}
            </h2>
            <p style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 400,
              fontSize: '18px',
              lineHeight: '1.5',
              color: '#6B7280',
              margin: 0
            }}>
              {t('register.petloving')}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '12px',
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <p style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: '14px',
                color: '#EF4444',
                margin: 0
              }}>
                {error}
              </p>
            </div>
          )}

          {/* Password Field */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 500,
              fontSize: '16px',
              color: '#374151',
              display: 'block',
              marginBottom: '10px',
              textAlign: isRTL ? "right" : "left"
            }}>
              {t('resetPassword.form.labels.newPassword')}
            </label>
            <div style={{
              position: 'relative',
              width: '100%'
            }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={t('resetPassword.form.placeholders.newPassword')}
                style={{
                  width: '100%',
                  height: '56px',
                  padding: isRTL ? '0 16px 0 50px' : '0 50px 0 16px',
                  border: '1.5px solid #E5E7EB',
                  borderRadius: '14px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '16px',
                  color: '#1F2937',
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  background: '#FFFFFF',
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left'
                }}
                onFocus={(e) => e.target.style.borderColor = config.primaryColor}
                onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  ...(isRTL ? { left: '16px', right: 'auto' } : { right: '16px', left: 'auto' })
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d={showPassword ? "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" : "M9.88 9.88a3 3 0 1 0 4.24 4.24m-1.8-8.35A11.14 11.14 0 0 1 12 5c7 0 11 7 11 7a11.14 11.14 0 0 1-2.07 2.65M3 3l18 18M10.73 10.73A3 3 0 1 0 13.27 13.27M8.59 8.59A11.14 11.14 0 0 0 1 12s4 7 11 7a11.14 11.14 0 0 0 2.41-.28"} stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  {showPassword && <circle cx="12" cy="12" r="3" stroke="#9CA3AF" strokeWidth="2" />}
                </svg>
              </button>
            </div>

            {/* Password Strength Indicator */}
            {formData.password && (
              <div style={{ marginTop: '12px' }}>
                {/* Strength Bar */}
                <div style={{
                  width: '100%',
                  height: '4px',
                  background: '#E5E7EB',
                  borderRadius: '2px',
                  marginBottom: '8px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${(passwordStrength.score / 5) * 100}%`,
                    height: '100%',
                    background: getStrengthColor(passwordStrength.score),
                    transition: 'all 0.3s ease'
                  }}></div>
                </div>

                {/* Strength Text */}
                <div style={{
                  display: 'flex',
                  justifyContent: isRTL ? 'flex-end' : 'flex-start',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: getStrengthColor(passwordStrength.score)
                  }}>
                    {getStrengthText(passwordStrength.score)}
                  </span>
                </div>

                {/* Requirements List */}
                {passwordStrength.feedback.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <p style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: '12px',
                      color: '#6B7280',
                      margin: '0 0 4px 0',
                      textAlign: isRTL ? 'right' : 'left'
                    }}>
                      {t('resetPassword.passwordStrength.requirementsTitle')}
                    </p>
                    <div style={{
                      display: 'flex',
                      flexDirection: isRTL ? 'row-reverse' : 'row',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      {Object.entries(passwordStrength.requirements).map(([key, met]) => (
                        <span
                          key={key}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '12px',
                            color: met ? '#10B981' : '#EF4444',
                            fontFamily: '"Red Hat Display", sans-serif'
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path
                              d={met ? "M9 12l2 2 4-4" : "M18 6L6 18M6 6l12 12"}
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          {requirementLabelMap[key]}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Confirm Password Field - Increased spacing */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 500,
              fontSize: '16px',
              color: '#374151',
              display: 'block',
              marginBottom: '10px',
              textAlign: isRTL ? "right" : "left"
            }}>
              {t('resetPassword.form.labels.confirmPassword')}
            </label>
            <div style={{
              position: 'relative',
              width: '100%'
            }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder={t('resetPassword.form.placeholders.confirmPassword')}
                style={{
                  width: '100%',
                  height: '56px',
                  padding: isRTL ? '0 16px 0 50px' : '0 50px 0 16px',
                  border: '1.5px solid #E5E7EB',
                  borderRadius: '14px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '16px',
                  color: '#1F2937',
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'border-color 0.2s ease',
                  background: '#FFFFFF',
                  direction: isRTL ? 'rtl' : 'ltr',
                  textAlign: isRTL ? 'right' : 'left'
                }}
                onFocus={(e) => e.target.style.borderColor = config.primaryColor}
                onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  ...(isRTL ? { left: '16px', right: 'auto' } : { right: '16px', left: 'auto' })
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d={showConfirmPassword ? "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" : "M9.88 9.88a3 3 0 1 0 4.24 4.24m-1.8-8.35A11.14 11.14 0 0 1 12 5c7 0 11 7 11 7a11.14 11.14 0 0 1-2.07 2.65M3 3l18 18M10.73 10.73A3 3 0 1 0 13.27 13.27M8.59 8.59A11.14 11.14 0 0 0 1 12s4 7 11 7a11.14 11.14 0 0 0 2.41-.28"} stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  {showConfirmPassword && <circle cx="12" cy="12" r="3" stroke="#9CA3AF" strokeWidth="2" />}
                </svg>
              </button>
            </div>

            {/* Password Match Indicator */}
            {formData.confirmPassword && (
              <div style={{ marginTop: '8px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexDirection: isRTL ? 'row-reverse' : 'row'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d={formData.password === formData.confirmPassword ? "M9 12l2 2 4-4" : "M18 6L6 18M6 6l12 12"}
                      stroke={formData.password === formData.confirmPassword ? '#10B981' : '#EF4444'}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: '14px',
                    color: formData.password === formData.confirmPassword ? '#10B981' : '#EF4444'
                  }}>
                    {formData.password === formData.confirmPassword
                      ? t('resetPassword.form.matches')
                      : t('resetPassword.form.errors.mismatch')}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button - Centered and narrower */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '6px'
          }}>
            <button
              onClick={handleCreatePassword}
              disabled={isLoading || passwordStrength.score < 5 || formData.password !== formData.confirmPassword}
              style={{
                width: '240px',
                height: '56px',
                background: (isLoading || passwordStrength.score < 5 || formData.password !== formData.confirmPassword)
                  ? '#D1D5DB' : config.buttonColor,
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '14px',
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                fontSize: '18px',
                cursor: (isLoading || passwordStrength.score < 5 || formData.password !== formData.confirmPassword)
                  ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: (isLoading || passwordStrength.score < 5 || formData.password !== formData.confirmPassword)
                  ? 'none' : `0 4px 12px ${config.buttonColor}30`
              }}
            >
              {isLoading && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={isRTL ? { marginLeft: '8px' } : { marginRight: '8px' }}>
                  <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <animateTransform
                      attributeName="transform"
                      attributeType="XML"
                      type="rotate"
                      dur="1s"
                      from="0 12 12"
                      to="360 12 12"
                      repeatCount="indefinite"
                    />
                  </path>
                </svg>
              )}
              {isLoading ? t('register.buttons.creating') : t('register.next')}
            </button>
          </div>
        </div>
      </div>

      {/* Responsive Styles */}
      <style>{`
        /* Hide bubbles on all screens */
        .create-password-bubble {
          display: none !important;
        }
        
        @media (max-width: 768px) {
          .create-password-responsive-root {
            flex-direction: column;
            padding-top: 0;
          }
          
          .create-password-header {
            display: none !important;
          }
          
          .create-password-left-panel {
            display: none !important;
          }
          
          .create-password-right-panel {
            width: 100% !important;
            height: auto !important;
            min-height: 100vh;
            padding: 60px 24px 40px 24px !important;
          }
          
          /* Adjust progress steps */
          .create-password-right-panel > div[style*="position: absolute"][style*="top: '140px'"] {
            position: relative !important;
            top: 0 !important;
            margin-bottom: 32px !important;
          }
          
          /* Adjust form content */
          .create-password-right-panel > div[style*="position: absolute"][style*="top: '220px'"] {
            position: relative !important;
            top: 0 !important;
            padding: 0 !important;
          }
        }
        
        @media (max-width: 480px) {
          .create-password-right-panel {
            padding: 40px 16px 32px 16px !important;
          }
        }
      `}</style>
    </div>
  )
}

export default CreatePassword